import React from "react";
import { IoGridOutline, IoKeypadOutline, IoSettingsOutline } from "react-icons/io5";
import {
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  ACTION_ITEMS_SETTINGS_TAB_KEYBOARD,
} from "./actionItemHelpers";

const SETTINGS_TABS = [
  { id: ACTION_ITEMS_SETTINGS_TAB_GENERAL, label: "General", Icon: IoSettingsOutline },
  { id: ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS, label: "Connectors", Icon: IoGridOutline },
  { id: ACTION_ITEMS_SETTINGS_TAB_KEYBOARD, label: "Shortcuts", Icon: IoKeypadOutline },
];

/**
 * Left settings navigation rail shared by settings and connector setup pages.
 * Collapse is controlled by the shell chrome toggle (same top level as main tasks).
 *
 * @example
 * <BusinessAppSettingsNav
 *   activeTab="connect-apps"
 *   onTabChange={(tab) => navigate(buildActionItemsSettingsPath({ tab }))}
 *   isCollapsed={false}
 * />
 * // Expanded: "Settings" title + tab list (full-height rail)
 *
 * @example
 * <BusinessAppSettingsNav activeTab="general" isCollapsed />
 * // Collapsed: rail width 0 / hidden; chrome toggle remains to expand again
 *
 * @param {object} props
 * @param {string} props.activeTab
 * @param {(tab: string) => void} [props.onTabChange]
 * @param {boolean} [props.isCollapsed=false]
 */
const BusinessAppSettingsNav = ({
  activeTab,
  onTabChange,
  isCollapsed = false,
}) => (
  <nav
    className={`business-app-settings-nav${
      isCollapsed ? " business-app-settings-nav--hidden" : ""
    }`}
    aria-label="Settings sections"
    aria-hidden={isCollapsed ? true : undefined}
  >
    <div className="business-app-settings-nav__head">
      <h2 className="business-app-settings-nav__title">Settings</h2>
    </div>
    <div className="business-app-settings-nav__list" role="list">
      {SETTINGS_TABS.map((tab) => {
        const TabIcon = tab.Icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="listitem"
            className={`business-app-settings-nav__item${
              isActive ? " business-app-settings-nav__item--active" : ""
            }`}
            aria-current={isActive ? "page" : undefined}
            aria-label={tab.label}
            title={tab.label}
            onClick={() => onTabChange?.(tab.id)}
            tabIndex={isCollapsed ? -1 : undefined}
          >
            <TabIcon className="business-app-settings-nav__icon" aria-hidden="true" />
            <span className="business-app-settings-nav__label">{tab.label}</span>
          </button>
        );
      })}
    </div>
  </nav>
);

export default BusinessAppSettingsNav;
